import { useMemo } from "react"
import {
  Milestone,
  STATUS_META,
  diffDays,
  addDaysIso,
  formatShortDate,
  formatDate,
  getAssignees,
  formatAssignees,
} from "../hooks/useMilestones"

interface MilestoneGanttProps {
  milestones: Milestone[]
  onEdit: (m: Milestone) => void
}

// 하루당 픽셀 폭 (타임라인 밀도)
const DAY_WIDTH = 28
// 산출물 2줄 + 담당자 표시를 위해 행 높이 확대
const ROW_HEIGHT = 78
// 마일스톤 이름 + 산출물이 잘리지 않도록 라벨 영역 확대
const LABEL_WIDTH = 340

export function MilestoneGantt({ milestones, onEdit }: MilestoneGanttProps) {
  // 타임라인 범위 계산 (가장 빠른 시작일 ~ 가장 늦은 종료일 + slack)
  const timeline = useMemo(() => {
    if (milestones.length === 0) return null

    let min = milestones[0].startDate
    let max = milestones[0].dueDate

    milestones.forEach((m) => {
      if (m.startDate < min) min = m.startDate
      const endWithSlack = addDaysIso(m.dueDate, m.slackDays ?? 0)
      if (endWithSlack > max) max = endWithSlack
    })

    // 앞뒤 3일 여백
    const rangeStart = addDaysIso(min, -3)
    const rangeEnd = addDaysIso(max, 3)
    const totalDays = Math.max(1, diffDays(rangeStart, rangeEnd) + 1)

    // 날짜 눈금 (매일)
    const ticks: { iso: string; isMonthStart: boolean; isWeekend: boolean }[] = []
    for (let i = 0; i < totalDays; i++) {
      const iso = addDaysIso(rangeStart, i)
      const d = new Date(iso.slice(0, 10))
      ticks.push({
        iso,
        isMonthStart: d.getUTCDate() === 1,
        isWeekend: d.getUTCDay() === 0 || d.getUTCDay() === 6,
      })
    }

    return { rangeStart, totalDays, ticks }
  }, [milestones])

  // 오늘 위치 (타임라인 내에 있을 때만 표시)
  const todayOffset = useMemo(() => {
    if (!timeline) return null
    const today = new Date().toISOString()
    const offset = diffDays(timeline.rangeStart, today)
    if (offset < 0 || offset >= timeline.totalDays) return null
    return offset
  }, [timeline])

  if (!timeline) {
    return (
      <div className="py-16 text-center">
        <div className="w-14 h-14 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
        </div>
        <p className="text-gray-500 font-medium">등록된 마일스톤이 없습니다</p>
        <p className="text-sm text-gray-400 mt-1">마일스톤을 추가하면 간트 차트가 표시됩니다.</p>
      </div>
    )
  }

  const chartWidth = timeline.totalDays * DAY_WIDTH

  // 월 헤더 그룹 계산
  const monthGroups: { label: string; span: number }[] = []
  timeline.ticks.forEach((t) => {
    const d = new Date(t.iso.slice(0, 10))
    const label = `${d.getUTCFullYear()}년 ${d.getUTCMonth() + 1}월`
    const last = monthGroups[monthGroups.length - 1]
    if (last && last.label === label) {
      last.span += 1
    } else {
      monthGroups.push({ label, span: 1 })
    }
  })

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* 범례 */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
        <span className="font-semibold text-gray-700">범례</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-6 h-2.5 rounded bg-orange-500" />
          <span className="text-gray-600">계획 기간</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-6 h-2.5 rounded bg-amber-200 border border-dashed border-amber-500" />
          <span className="text-gray-600">여유(Slack) · 예상 지연</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-0.5 h-3.5 bg-red-500" />
          <span className="text-gray-600">오늘</span>
        </span>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: LABEL_WIDTH + chartWidth }}>
          {/* 헤더: 월 + 일 */}
          <div className="flex sticky top-0 z-20 bg-white">
            <div
              className="shrink-0 border-r border-b border-gray-200 bg-gray-50 px-3 flex items-end pb-1.5"
              style={{ width: LABEL_WIDTH }}
            >
              <span className="text-xs font-semibold text-gray-600">마일스톤 / 산출물</span>
            </div>
            <div className="relative">
              {/* 월 행 */}
              <div className="flex border-b border-gray-200 bg-gray-50">
                {monthGroups.map((g, i) => (
                  <div
                    key={`${g.label}-${i}`}
                    className="text-xs font-semibold text-gray-700 px-2 py-1 border-r border-gray-200 whitespace-nowrap overflow-hidden"
                    style={{ width: g.span * DAY_WIDTH }}
                  >
                    {g.label}
                  </div>
                ))}
              </div>
              {/* 일 행 */}
              <div className="flex border-b border-gray-200">
                {timeline.ticks.map((t) => {
                  const d = new Date(t.iso.slice(0, 10))
                  return (
                    <div
                      key={t.iso}
                      className={`text-[10px] text-center py-1 border-r border-gray-100 ${
                        t.isWeekend ? "bg-gray-50 text-gray-400" : "text-gray-500"
                      }`}
                      style={{ width: DAY_WIDTH }}
                    >
                      {d.getUTCDate()}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* 바디: 마일스톤 행 */}
          <div className="relative">
            {/* 오늘 세로선 */}
            {todayOffset !== null ? (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                style={{ left: LABEL_WIDTH + todayOffset * DAY_WIDTH + DAY_WIDTH / 2 }}
              />
            ) : null}

            {milestones.map((m) => {
              const meta = STATUS_META[m.status] || STATUS_META.todo
              const offset = diffDays(timeline.rangeStart, m.startDate)
              const duration = Math.max(1, diffDays(m.startDate, m.dueDate) + 1)
              const slack = Math.max(0, m.slackDays ?? 0)
              const progress = Math.min(100, Math.max(0, m.progress ?? 0))
              // 지연 여부: 완료 전 + 목표일 경과
              const isOverdue =
                m.status !== "completed" &&
                m.status !== "archive" &&
                m.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10)
              const assignees = getAssignees(m)
              const assigneeLabel = formatAssignees(assignees)
              // 라벨 폭을 고려해 3명까지 노출하고 나머지는 +N 으로 축약
              const shownAssignees = assignees.slice(0, 3)
              const restAssignees = assignees.length - shownAssignees.length

              return (
                <div
                  key={m._id}
                  className="flex border-b border-gray-100 hover:bg-orange-50/40 transition-colors group"
                  style={{ height: ROW_HEIGHT }}
                >
                  {/* 라벨 */}
                  <div
                    className="shrink-0 border-r border-gray-200 px-3 py-2 flex flex-col justify-center gap-0.5 cursor-pointer"
                    style={{ width: LABEL_WIDTH }}
                    onClick={() => onEdit(m)}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                      <span
                        className="text-sm font-medium text-gray-900 truncate group-hover:text-orange-700"
                        title={m.title}
                      >
                        {m.title}
                      </span>
                      {isOverdue ? (
                        <span className="shrink-0 text-[10px] font-bold text-red-600 bg-red-50 px-1 rounded">
                          지연
                        </span>
                      ) : null}
                    </div>
                    <div
                      className={`text-[11px] leading-snug overflow-hidden ${
                        m.deliverable ? "text-gray-600" : "text-gray-400 italic"
                      }`}
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                      }}
                      title={m.deliverable ? `산출물: ${m.deliverable}` : "산출물 미기입"}
                    >
                      {m.deliverable ? `산출물: ${m.deliverable}` : "산출물 미기입"}
                    </div>

                    {/* 담당자 (복수 지원) */}
                    {assignees.length > 0 ? (
                      <div
                        className="flex items-center gap-1 min-w-0"
                        title={`담당자: ${assigneeLabel}`}
                      >
                        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="flex items-center gap-1 min-w-0">
                          {shownAssignees.map((a, i) => (
                            <span
                              key={`${a.userId || a.userName}-${i}`}
                              className="text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 truncate max-w-[80px]"
                            >
                              {a.userName}
                            </span>
                          ))}
                          {restAssignees > 0 ? (
                            <span className="text-[10px] text-gray-500 bg-gray-100 rounded px-1 py-0.5 shrink-0">
                              +{restAssignees}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  {/* 타임라인 영역 */}
                  <div className="relative flex-1" style={{ width: chartWidth }}>
                    {/* 주말 배경 */}
                    <div className="absolute inset-0 flex">
                      {timeline.ticks.map((t) => (
                        <div
                          key={t.iso}
                          className={`border-r border-gray-100 ${t.isWeekend ? "bg-gray-50/70" : ""}`}
                          style={{ width: DAY_WIDTH }}
                        />
                      ))}
                    </div>

                    {/* 계획 막대 */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 flex items-center cursor-pointer"
                      style={{ left: offset * DAY_WIDTH + 2 }}
                      onClick={() => onEdit(m)}
                      title={`${m.title}\n${formatDate(m.startDate)} ~ ${formatDate(m.dueDate)}${slack > 0 ? ` (+${slack}일 여유)` : ""}\n진행률 ${progress}%${assigneeLabel ? `\n담당자 ${assigneeLabel}` : ""}`}
                    >
                      <div
                        className={`relative h-5 rounded ${meta.bar} shadow-sm overflow-hidden`}
                        style={{ width: duration * DAY_WIDTH - 4 }}
                      >
                        {/* 진행률 오버레이 */}
                        {progress > 0 ? (
                          <div
                            className="absolute inset-y-0 left-0 bg-white/35"
                            style={{ width: `${progress}%` }}
                          />
                        ) : null}
                        <span className="absolute inset-0 flex items-center px-1.5 text-[10px] font-semibold text-white whitespace-nowrap overflow-hidden">
                          {progress > 0 ? `${progress}%` : ""}
                        </span>
                      </div>

                      {/* Slack(예상 지연) 구간 */}
                      {slack > 0 ? (
                        <div
                          className="h-5 rounded-r border border-dashed border-amber-500 bg-amber-100/80 flex items-center justify-center"
                          style={{ width: slack * DAY_WIDTH }}
                          title={`여유(Slack) ${slack}일`}
                        >
                          <span className="text-[10px] font-semibold text-amber-700 whitespace-nowrap">
                            +{slack}d
                          </span>
                        </div>
                      ) : null}

                      {/* 기간 라벨 */}
                      <span className="ml-2 text-[11px] text-gray-500 whitespace-nowrap">
                        {formatShortDate(m.startDate)} ~ {formatShortDate(m.dueDate)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
